import { DataVersion } from './data-version';
import { MotionDetectionWindow } from './location';
import { UnifiedMonitoringProduct } from './unified-monitoring';

export enum SOPTalkdownType {
  STATIC = 'static',
  DYANMIC = 'dynamic',
}

export type SOPTalkdown = {
  type: SOPTalkdownType;
  text: string;
};

export enum SituationColor {
  RED = 'red',
  GREEN = 'green',
  BLUE = 'blue',
}

export interface Situation {
  label: string;
  color: SituationColor;
  subSituations?: string[];
  cameras?: string[];
  schedules?: MotionDetectionWindow;
}

export interface TimeRange {
  startTime: string;
  endTime: string;
}

export interface SOPNote {
  text: string;
  isRelevantForAiOperator: boolean;
  cameras?: string[];
  timeRange?: TimeRange;
}

export interface SOPException {
  text: string;
  isRelevantForAiOperator: boolean;
  cameras?: string[];
  timeRange?: TimeRange;
}

export interface BusinessHours {
  hours: string;
  timezone: string;
}

export interface QuickResolveAction {
  label: string;
  subActions?: string[];
}

export type QuickResolveActionItem = string | QuickResolveAction;

export type SOPWorkflow = {
  firstTalkdown?: SOPTalkdown;
  secondTalkdown?: SOPTalkdown;
  exceptions: SOPException[];
  siteAddress: string;
  siteGoogleMapLocation: string;
  siteCategory?: string;
  siteDescription?: string;
  businessHours: BusinessHours;
  isTalkdownEnabled: boolean;
  escalationPoints: string[];
  notes: SOPNote[];
  situations: Situation[];
  escalationProtocol: string[];
  quickResolveActions?: QuickResolveActionItem[];
  isZeroTolerance?: boolean;
  isLongRangeAnalysisEnabled?: boolean;
  talkdowns?: SOPTalkdown[];
  talkdownNotes?: string[];
};

export type ScanSOPContact = {
  name: string;
  phoneNumber: string;
  role?: string;
};

export type ScanTenantSOP = {
  address: string;
  emergencySituations: string[];
  nonEmergencySituations: string[];
  exceptions: string[];
  emergencyContact: ScanSOPContact[];
  nonEmergencyContact: ScanSOPContact[];
  isTalkdownEnabled?: boolean;
};

export interface SOPDTO {
  id?: string;
  sop_workflow?: SOPWorkflow;
  scan_sop?: ScanTenantSOP;
  alarm_type_id?: string;
  map_embed_url?: string;
  updated_at_utc?: string;
}

export interface UpdateSOPPayload {
  sop_text: string;
  alarm_type_id?: string;
  clear_alarm_type?: boolean;
}

export interface CreateSOPPayload {
  sop_text: string;
  tenant_id: string;
  location_id: string;
  alarm_type_id?: string;
  clear_alarm_type?: boolean;
}

export interface SOPWorkflowState {
  logCallFromSOP: (twilioCallSid: string, contactTag?: string) => void;
}

export interface SOPWorkflowFormItemsType {
  title: string;
  id: keyof SOPWorkflow;
  inputType: string;
  info?: string;
  isFullColSpan?: boolean;
  showTimezone?: boolean;
  dropdownOptions?: SiteCategoryOption[];
}

export interface SiteCategoryOption {
  key: string;
  description: string;
}

export interface EvidenceUploaderOption {
  locationAlarmId: string;
  product?: UnifiedMonitoringProduct;
  timezone?: string;
  onUploadSuccess?: () => void;
  /**
   * Overrides the alarm-group context. Callers rendered outside an
   * AlarmGroupProvider (e.g. the incident details page) have no context to
   * read, so without this their writes silently target v2.
   */
  dataVersion?: DataVersion;
}
